/**
 * ProductCard — an image-first catalog grid tile (donate-shop layout).
 *
 * Presentation over a {@link ProductListItem}: a large square cover at the top, then
 * the server-computed price (bold) and the product title beneath. A struck-through
 * original price + "−N%" badge render *only* when the server denormalises those
 * fields onto the list payload — we never fabricate discounts. The rating, when
 * present, sits as a small badge on the cover.
 *
 * Navigation is delegated to ``onSelect`` (the product page is where the order is placed)
 * so the entity layer never touches routing. Prices/ratings come from the server and are
 * never re-derived here.
 */
import { Card, CurrencyIcon, Price, Stars } from "@melusine/shared-ui";

import type { ProductListItem } from "../model";
import styles from "./ProductCard.module.css";

export interface ProductCardProps {
  readonly product: ProductListItem;
  readonly onSelect: (product: ProductListItem) => void;
  /**
   * Cart mode (ТЗ §8): when provided, the "+" adds the product to the local cart instead of
   * opening it, the card highlights while selected, and a − N + stepper replaces the "+".
   */
  readonly cart?: {
    /** Units currently selected for this product (0 = not in cart). */
    readonly qty: number;
    readonly onAdd: (product: ProductListItem) => void;
    readonly onIncrement: (productId: number) => void;
    readonly onDecrement: (productId: number) => void;
  };
}

const MinusIcon = (): JSX.Element => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.6"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M5 12h14" />
  </svg>
);

const PlusIcon = (): JSX.Element => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.6"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </svg>
);

/**
 * Optional fields the server may denormalise onto the list payload. They render only
 * when present, so we never advertise offers the API didn't return.
 */
interface ProductCardExtras {
  /** Original RUB price (DRF Decimal string) before a discount, if any. */
  readonly original_price?: string | null;
  /** Integer discount percentage, if any. */
  readonly discount_percent?: number | null;
  readonly primogem_cap?: string | null;
  readonly star_glitter_allowed?: boolean;
}

/** Russian plural for "отзыв" (1 отзыв / 2 отзыва / 5 отзывов). */
function reviewsWord(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) {
    return "отзыв";
  }
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
    return "отзыва";
  }
  return "отзывов";
}

function discountPercent(extras: ProductCardExtras, base: string): number | null {
  if (typeof extras.discount_percent === "number" && extras.discount_percent > 0) {
    return Math.round(extras.discount_percent);
  }
  const original = extras.original_price != null ? Number(extras.original_price) : NaN;
  const current = Number(base);
  if (Number.isFinite(original) && Number.isFinite(current) && original > current) {
    return Math.round((1 - current / original) * 100);
  }
  return null;
}

export function ProductCard({
  product,
  onSelect,
  cart,
}: ProductCardProps): JSX.Element {
  const select = (): void => {
    onSelect(product);
  };

  const selectedQty = cart?.qty ?? 0;
  const isSelected = selectedQty > 0;
  // ТЗ §6: the stepper never exceeds the product's per-order limit; «+» blocks at the cap.
  const maxQty = product.max_qty_per_order;
  const atMax = selectedQty >= maxQty;

  const extras = product as ProductListItem & ProductCardExtras;
  const showGlitter = extras.star_glitter_allowed === true;
  const percentOff = discountPercent(extras, product.base_price);
  const hasOriginal =
    percentOff !== null && extras.original_price != null
      ? extras.original_price
      : undefined;

  return (
    <Card
      interactive
      className={styles.card}
      data-selected={isSelected}
      role="button"
      tabIndex={0}
      onClick={select}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          select();
        }
      }}
    >
      <div className={styles.media}>
        {product.image_url ? (
          <img src={product.image_url} alt="" className={styles.image} />
        ) : (
          <span className={styles.placeholder} aria-hidden="true">
            ✦
          </span>
        )}
        {percentOff !== null ? (
          <span className={styles.discount}>−{percentOff}%</span>
        ) : null}
        {product.reviews_count > 0 ? (
          <span className={styles.ratingBadge}>
            <Stars
              value={Number(product.rating_avg)}
              size="sm"
              showValue
            />
          </span>
        ) : null}
        {showGlitter ? (
          <span className={styles.glitter} aria-label="Можно оплатить блеском">
            <CurrencyIcon kind="glitter" size={12} />
          </span>
        ) : null}
        {isSelected ? (
          <span className={styles.selectedBadge}>Выбрано · {selectedQty}</span>
        ) : null}
        {cart === undefined ? (
          <button
            type="button"
            className={styles.add}
            aria-label="Открыть товар"
            onClick={(event) => {
              event.stopPropagation();
              select();
            }}
          >
            <PlusIcon />
          </button>
        ) : isSelected ? (
          <div
            className={styles.stepper}
            role="group"
            aria-label={`Количество: ${selectedQty}`}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className={styles.stepBtn}
              aria-label="Убрать один"
              onClick={(event) => {
                event.stopPropagation();
                cart.onDecrement(product.id);
              }}
            >
              <MinusIcon />
            </button>
            <span className={styles.stepCount}>{selectedQty}</span>
            <button
              type="button"
              className={styles.stepBtn}
              aria-label="Добавить ещё один"
              disabled={atMax}
              title={atMax ? `Максимум ${maxQty} шт. в заказе` : undefined}
              onClick={(event) => {
                event.stopPropagation();
                if (atMax) {
                  return;
                }
                cart.onIncrement(product.id);
              }}
            >
              <PlusIcon />
            </button>
          </div>
        ) : (
          <button
            type="button"
            className={styles.add}
            aria-label="Добавить в корзину"
            onClick={(event) => {
              event.stopPropagation();
              cart.onAdd(product);
            }}
          >
            <PlusIcon />
          </button>
        )}
      </div>
      <div className={styles.body}>
        <span className={styles.price}>
          <Price
            amount={product.base_price}
            {...(hasOriginal !== undefined ? { original: hasOriginal } : {})}
            accent
            size="sm"
          />
        </span>
        <span className={styles.title}>{product.title}</span>
        {product.reviews_count > 0 ? (
          <span className={styles.rating}>
            {Number(product.rating_avg).toFixed(1)} · {product.reviews_count}{" "}
            {reviewsWord(product.reviews_count)}
          </span>
        ) : null}
      </div>
    </Card>
  );
}
